import { House, LogIn, Trophy, UserPlus } from "lucide-react";
import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link } from "react-router";

import { LinkAction } from "../components/Screen.js";
import { Slices, TallyStats } from "../components/game/TallySheet.js";
import { useSession } from "../hooks/useSession.js";
import {
  API_ERROR,
  HttpError,
  fetchMatches,
  fetchProgression,
  type Career,
  type MatchLine,
} from "../services/http.js";

/**
 * What you are good at, across every party — `/me`.
 *
 * # The screen phase 8 computed and never showed
 *
 * `src/progression/` folds the round log into a career, `GET /api/me/progression`
 * has served it since phase 8, and `fetchProgression()` had **no caller** for
 * four phases. "Progression that carries across parties" is a headline claim of
 * this project and it was a table nobody could read. Fifth instance of *"a thing
 * nothing can select is not shipped"*.
 *
 * # It needs an account, and that is ADR 0007 rather than a gap
 *
 * Playing never needs one — a guest types a name and is in. A career is one of
 * the two things an account is *for* (the other is owning a party), because
 * history keys off a profile that outlives the room. So a signed-out visitor is
 * told what an account would give them, not refused.
 *
 * # Counts, never rates
 *
 * Everything on this page is *"7 of 9"*, the same as the recap and for the same
 * reason — the denominator is **rounds you were scored in**, which in Classic is
 * roughly your share of the turns rather than the length of the evening.
 */
export function CareerPage() {
  const { t } = useTranslation();
  const { account } = useSession();
  const [career, setCareer] = useState<Career | null>(null);
  // Phase 16j-1. A separate request from the career, because it is a separate
  // route: `Career` is a pure computed type and rows do not belong in it. Its
  // failure is not the page's failure — the aggregates are still worth showing.
  const [matches, setMatches] = useState<MatchLine[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!account) {
      return;
    }
    let cancelled = false;
    void fetchMatches().then(
      (loaded) => {
        if (!cancelled) {
          setMatches(loaded);
        }
      },
      () => {
        // Deliberately silent. A career that renders its aggregates and not its
        // list is degraded; a career that renders an error instead of both is
        // broken.
      },
    );
    void fetchProgression().then(
      (loaded) => {
        if (!cancelled) {
          setCareer(loaded);
        }
      },
      (cause: unknown) => {
        if (cancelled) {
          return;
        }
        // A session that expired between the two requests is not an error to
        // show — `useSession` will have the same answer on the next render.
        if (cause instanceof HttpError && cause.code === API_ERROR.unauthenticated) {
          return;
        }
        setError(cause instanceof Error ? cause.message : t("career.loadFailed"));
      },
    );
    return () => {
      cancelled = true;
    };
  }, [account, t]);

  // Three states, not two: `undefined` is still asking, and a screen that
  // flashed "sign in to see your career" at somebody signed in is worse than a
  // slower one.
  if (account === undefined) {
    return (
      <Frame>
        <p className="text-sm text-white/50">{t("career.loading")}</p>
      </Frame>
    );
  }

  if (account === null) {
    return (
      <Frame>
        <p className="text-sm text-white/60">{t("career.needsAccount")}</p>
        <div className="mt-4 flex flex-col gap-3">
          <LinkAction to="/login" icon={LogIn} accent>
            {t("login.title")}
          </LinkAction>
          <LinkAction to="/register" icon={UserPlus}>
            {t("login.createAccount")}
          </LinkAction>
        </div>
      </Frame>
    );
  }

  if (error) {
    return (
      <Frame>
        <p role="alert" className="text-sm text-wrong-500">
          {error}
        </p>
      </Frame>
    );
  }

  if (!career) {
    return (
      <Frame>
        <p className="text-sm text-white/50">{t("career.reading")}</p>
      </Frame>
    );
  }

  if (career.games === 0) {
    return (
      <Frame>
        <p className="text-sm text-white/60">
          <Trans i18nKey="career.empty" components={{ em: <em /> }} />
        </p>
        <LinkAction to="/" icon={House} accent>
          {t("career.startOne")}
        </LinkAction>
      </Frame>
    );
  }

  const named = [career.best_decade, career.worst_decade].filter(
    (key): key is string => key !== null,
  );
  const label = (key: string | null) =>
    career.by_decade.find((bucket) => bucket.key === key)?.label ?? key;

  return (
    <Frame>
      <section aria-label={t("career.games")} className="flex items-baseline gap-4">
        <p className="text-3xl font-semibold tabular-nums">
          {career.games}
          <span className="ml-2 text-sm font-normal text-white/50">
            {t("career.gamesUnit", { count: career.games })}
          </span>
        </p>
        <p className="text-3xl font-semibold tabular-nums text-flash-400">
          {career.games_won}
          <span className="ml-2 text-sm font-normal text-white/50">{t("recap.won")}</span>
        </p>
      </section>

      <section aria-label={t("career.overall")} className="mt-6">
        <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-white/50">
          {t("career.acrossRounds", { count: career.overall.rounds })}
        </h2>
        <TallyStats tally={career.overall} />
        <p className="mt-2 text-xs text-white/40 tabular-nums">
          {t("career.cardsWon", { count: career.overall.cards_won })} ·{" "}
          {t("career.coinsEarned", { count: career.overall.coins })}
        </p>
      </section>

      {/* Named in words rather than only highlighted below, because this is the
          one line most people will read. `null` until the server has enough
          rounds to mean it — "worst at the 1960s" off a single card is the sort
          of thing people repeat. */}
      {(career.best_decade ?? career.worst_decade) && (
        <p className="mt-4 text-sm text-white/70">
          {career.best_decade &&
            t("career.strongest", { decade: label(career.best_decade) })}
          {career.best_decade && career.worst_decade && " "}
          {career.worst_decade &&
            t("career.weakest", { decade: label(career.worst_decade) })}
        </p>
      )}

      <Slices label={t("recap.byDecade")} buckets={career.by_decade} highlight={named} />
      <Slices
        label={t("recap.byGenre")}
        buckets={career.by_genre}
        unknown={career.genre_unknown}
      />

      <MatchList matches={matches} />

      {/* Phase 16j-2. A page nothing links to is a page nobody finds — which is
          the mistake this project has now made four times. */}
      <LinkAction to="/hall" icon={Trophy}>
        {t("career.hallLink")}
      </LinkAction>
    </Frame>
  );
}

/**
 * The games themselves — phase 16j-1.
 *
 * `Career` is aggregates only, so this page could say *you have won seven* and
 * not say *which*. The rows and the recaps have existed since phase 8; nothing
 * had ever asked for them as a list.
 *
 * ⚠️ **Counts, never rates.** Every line says what happened, and nothing here
 * divides by anything — the denominator on this page is *rounds you were scored
 * in*, which in Classic is roughly your share of the turns rather than the
 * length of the evening. A percentage here would quietly contradict every other
 * screen.
 */
function MatchList({ matches }: { matches: readonly MatchLine[] }) {
  const { t } = useTranslation();
  if (matches.length === 0) {
    return null;
  }

  return (
    <section
      className="mt-6"
      aria-label={t("career.matchesTitle")}
      data-testid="match-list"
    >
      <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-white/50">
        {t("career.matchesTitle")}
      </h2>
      <ol className="flex flex-col gap-1">
        {matches.map((match) => (
          <li key={match.game_id}>
            <Link
              to={`/recap/${match.game_id}`}
              className="flex items-baseline justify-between gap-3 rounded-lg px-2 py-1.5 text-sm hover:bg-white/5"
            >
              <span className="min-w-0 truncate">
                {match.party_name ?? t("career.matchUnnamed")}
                <span className="ml-2 text-xs uppercase tracking-wide text-white/40">
                  {match.mode}
                </span>
              </span>
              <span
                className={
                  match.won ? "shrink-0 text-correct-500" : "shrink-0 text-white/40"
                }
              >
                {match.won ? t("career.matchWon") : t("career.matchLost")}
              </span>
            </Link>
          </li>
        ))}
      </ol>
    </section>
  );
}

/** One frame for every state, so the header does not move as the page resolves. */
function Frame({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();

  return (
    <main className="mx-auto flex min-h-full w-full max-w-3xl flex-col gap-1 p-5 pb-16">
      <header className="mb-4">
        <p className="text-xs uppercase tracking-[0.2em] text-flash-500">
          {t("app.name")}
        </p>
        <h1 className="mt-1 text-3xl font-semibold">{t("menu.career")}</h1>
      </header>
      {children}
      <LinkAction to="/" icon={House}>
        ← {t("common.back")}
      </LinkAction>
    </main>
  );
}
